import { useState } from 'react';
import { api, errorMessage } from '../api/client';
import type { Changes, Disease, EventType, Organ } from '../api/types';
import { useResource } from '../api/useResource';
import {
  Field,
  Panel,
  Select,
  SlugPicker,
  Spinner,
  Status,
  TextInput,
  Toggle,
  summariseChanges,
} from '../components/ui';

const EVENT_TYPES: { value: EventType; label: string }[] = [
  { value: 'NORMAL', label: 'Normal' },
  { value: 'CANCER', label: 'Cancer (metastasises)' },
  { value: 'MOVE_BACKWARD', label: 'Move backward' },
  { value: 'EMBOLUS', label: 'Embolus' },
];

export function DiseasesPage() {
  const diseases = useResource(() => api<{ diseases: Disease[] }>('/v1/admin/diseases'));
  const organs = useResource(() => api<{ organs: Organ[] }>('/v1/admin/organs'));
  const [editing, setEditing] = useState<Disease | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const organOptions = (organs.data?.organs ?? []).map((organ) => ({
    slug: organ.slug,
    name: organ.name,
  }));

  async function save() {
    if (!editing) return;

    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const result = await api<{ changes: Changes }>(`/v1/admin/diseases/${editing.slug}`, {
        method: 'PATCH',
        body: {
          name: editing.name,
          colorHex: editing.colorHex,
          eventType: editing.eventType,
          description: editing.description,
          active: editing.active,
          metastasisOrganSlug: editing.metastasisOrganSlug,
          applicableOrganSlugs: editing.applicableOrganSlugs,
        },
      });

      setMessage(summariseChanges(result.changes));
      setEditing(null);
      await diseases.reload();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  if ((diseases.loading && !diseases.data) || (organs.loading && !organs.data)) return <Spinner />;
  if (diseases.error) return <Status error={diseases.error} />;

  const organCount = organOptions.length;

  return (
    <Panel title="Diseases" actions={<span className="muted">{diseases.data?.diseases.length ?? 0} diseases</span>}>
      <p className="muted">
        The organ checkboxes are the spec 2.7 <code>organDiseaseMap</code>, still marked{' '}
        <code>[OPEN]</code>. Every disease currently applies to all {organCount} organs — narrowing
        Cholesterol, Emboli or Trauma when the client confirms is a save here, not a release.
      </p>

      <table className="table">
        <thead>
          <tr>
            <th>Slug</th>
            <th>Name</th>
            <th>Event</th>
            <th>Organs</th>
            <th>Colour</th>
            <th>Active</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {diseases.data?.diseases.map((disease) => (
            <tr key={disease.slug} className={disease.active ? undefined : 'row-inactive'}>
              <td><code>{disease.slug}</code></td>
              <td>{disease.name}</td>
              <td>{disease.eventType}</td>
              <td className={disease.applicableOrganSlugs.length === 0 ? 'warn' : undefined}>
                {disease.applicableOrganSlugs.length} / {organCount}
              </td>
              <td>
                <span className="swatch" style={{ background: disease.colorHex }} />
                <code>{disease.colorHex}</code>
              </td>
              <td>{disease.active ? 'yes' : 'no'}</td>
              <td>
                <button className="button button-ghost" onClick={() => setEditing({ ...disease })}>
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing ? (
        <div className="editor">
          <h3>
            Editing <code>{editing.slug}</code>
          </h3>

          <div className="grid">
            <Field label="Name">
              <TextInput value={editing.name} onChange={(name) => setEditing({ ...editing, name })} />
            </Field>
            <Field label="Colour" hint="#RRGGBB">
              <TextInput
                value={editing.colorHex}
                onChange={(colorHex) => setEditing({ ...editing, colorHex })}
              />
            </Field>
            <Field label="Event type" hint="Spec 2.4 — how it resolves">
              <Select
                value={editing.eventType}
                options={EVENT_TYPES}
                onChange={(eventType) => setEditing({ ...editing, eventType })}
              />
            </Field>
            <Field label="Metastasis organ" hint="Only meaningful for CANCER">
              <Select
                value={editing.metastasisOrganSlug ?? ''}
                options={[
                  { value: '', label: '— none —' },
                  ...organOptions.map((organ) => ({ value: organ.slug, label: organ.name })),
                ]}
                onChange={(slug) => setEditing({ ...editing, metastasisOrganSlug: slug || null })}
              />
            </Field>
          </div>

          <Field label="Guide description">
            <textarea
              className="input"
              rows={4}
              value={editing.description}
              onChange={(event) => setEditing({ ...editing, description: event.target.value })}
            />
          </Field>

          <Field label="Applies to organs (spec 2.7)">
            <SlugPicker
              options={organOptions}
              selected={editing.applicableOrganSlugs}
              onChange={(applicableOrganSlugs) => setEditing({ ...editing, applicableOrganSlugs })}
            />
          </Field>
          {editing.applicableOrganSlugs.length === 0 ? (
            <p className="status status-error">
              No organs selected — this disease can never be drawn onto a board.
            </p>
          ) : null}

          <Toggle
            checked={editing.active}
            label="Active (visible to the app)"
            onChange={(active) => setEditing({ ...editing, active })}
          />

          <div className="row">
            <button className="button" onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save disease'}
            </button>
            <button className="button button-ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <Status error={error} message={message} />
    </Panel>
  );
}
